import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "../store/useAuth";
import { useLogout } from "../API/authApiservice";
import { isEmbedded } from "../utils/embed";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isRegistred, user } = useAuth();
  const { logout } = useLogout();
  // Allowed phone numbers
  const allowedPhoneNumbers = ["251938958554", "251936010720"];
  // Conditionally filter nav links based on authentication and phone number
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/specializations", label: "Specializations" },
    { href: "/find-doctor", label: "Doctors" },
    ...(isAuthenticated
      ? [{ href: "/appointments", label: "My Appointments" }]
      : []),
    {
      href: "/booking-guide",
      label: " Booking Guide",    },    {href:"/profile", label:"My Profile"},
    ...(isAuthenticated && allowedPhoneNumbers.includes(user?.phoneNumber ?? "")
      ? [
          { href: "/generate-qrcode", label: "QR Code Generator" }
        ]
      : []),
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const embedded = isEmbedded();

  // Embed mode: no logo — a compact vertical side menu with small links, so it
  // sits on the side of the booking content (Layout places this in a left rail).
  if (embedded) {
    return (
      <nav className="sticky top-0 p-2">
        <div className="flex flex-col space-y-0.5">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleLinkClick}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                location.pathname === link.href
                  ? "text-primary-100 bg-primary-50"
                  : "text-gray-600 hover:text-primary-100 hover:bg-gray-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            className="mt-2 self-start bg-primary-100 text-white/90 font-medium px-3 py-1.5 rounded-lg text-xs"
            onClick={() => {
              if (isAuthenticated) {
                logout();
              } else {
                navigate(isRegistred ? "/login" : "/signup");
              }
              handleLinkClick();
            }}
          >
            {isAuthenticated ? "Log out" : isRegistred ? "Sign in" : "Sign up"}
          </button>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/80 backdrop-blur-lg shadow-md" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <div className="flex-shrink-0 transform hover:scale-105 transition-transform">
              <Logo />
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:block ml-10">
              <div className="flex items-center space-x-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`relative px-3 py-2 text-sm font-medium group ${
                      location.pathname === link.href
                        ? "text-primary-100 underline"
                        : "text-gray-600 hover:text-primary-100"
                    }`}
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-100 transform scale-x-0 transition-transform group-hover:scale-x-100" />
                  </Link>
                ))}

         

                <button
                  className="bg-primary-100 text-white/90 font-medium px-4 py-2 rounded-xl"
                  onClick={() => {
                    if (isAuthenticated) {
                      logout();
                    } else {
                      navigate(isRegistred ? "/login" : "/signup");
                    }
                  }}
                >
                  {isAuthenticated
                    ? "Log out"
                    : isRegistred
                    ? "Sign in"
                    : "Sign up"}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-full text-gray-600 hover:text-primary-100 hover:bg-gray-50 transition-colors focus:outline-none"
            >
              {isOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out ${
          isOpen
            ? "max-h-screen opacity-100 visible"
            : "max-h-0 opacity-0 invisible"
        }`}
      >
        <div className="px-4 pt-2 pb-3 space-y-2 bg-white/80 backdrop-blur-lg border-t">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleLinkClick}
              className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors ${
                location.pathname === link.href
                  ? "text-primary-100 bg-primary-50 underline"
                  : "text-gray-600 hover:text-primary-100 hover:bg-gray-50"
              }`}
            >
              {link.label}
            </Link>
          ))}

  
          <button
            className="bg-primary-100 text-white/90 font-medium px-4 py-2 rounded-xl"
            onClick={() => {
              if (isAuthenticated) {
                logout();
              } else {
                navigate("/login");
              }
              handleLinkClick();
            }}
          >
            {isAuthenticated ? "Log out" : "Sign in"}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
